import type { SavingsAccountParams } from "./types/forms";

type Compounding = "monthly" | "yearly";

export class SavingsReceipt {
  readonly balance: number;
  readonly interest: number;

  constructor(balance: number, interest: number) {
    this.balance = Math.round(balance * 100) / 100;
    this.interest = Math.round(interest * 100) / 100;
  }
}

export interface SavingsTransaction {
  month: number;
  amountAdded: number;
  interestAdded: number;
  balanceAfter: number;
}

export class SavingsAccount {
  readonly name: string;
  balance: number;
  readonly interestRate: number;
  readonly transactions: SavingsTransaction[];
  private _currentMonthDeposit: number;
  private _currentMonthWithdrawal: number;

  constructor(config: SavingsAccountParams) {
    this.name = config.name ?? "Savings Account";
    this.balance = Math.round((config.initialAmount ?? 0) * 100) / 100;
    this.interestRate = config.interestRate ?? 0;
    this.transactions = [];
    this._currentMonthDeposit = 0;
    this._currentMonthWithdrawal = 0;
  }

  addMonthlySavings(
    amount: number,
    applyAtStart: boolean = false,
  ): SavingsReceipt {
    if (amount < 0)
      throw new Error("Monthly savings amount cannot be negative.");
    if (Math.round(amount * 100) / 100 !== amount)
      throw new Error(
        "Monthly savings amount must be rounded to two decimal places.",
      );

    this._currentMonthDeposit = amount;

    if (applyAtStart) {
      // Add deposit at start of month
      this.balance += amount;
      // Calculate interest on the full balance for the month
      const interest =
        Math.round(this.balance * (this.interestRate / 12 / 100) * 100) / 100;
      this.balance += interest;
      this.balance = Math.round(this.balance * 100) / 100;

      return new SavingsReceipt(this.balance, interest);
    } else {
      // Calculate interest on previous balance (before deposit)
      const interest =
        Math.round(this.balance * (this.interestRate / 12 / 100) * 100) / 100;
      // Then add deposit at end of month
      this.balance += interest + amount;
      this.balance = Math.round(this.balance * 100) / 100;

      return new SavingsReceipt(this.balance, interest);
    }
  }

  withdrawForMortgage(amount: number): void {
    if (amount < 0) throw new Error("Withdrawal amount cannot be negative.");
    if (Math.round(amount * 100) / 100 !== amount)
      throw new Error(
        "Withdrawal amount must be rounded to two decimal places.",
      );
    if (this.balance < amount)
      throw new Error("Not enough savings to make the withdrawal.");

    this._currentMonthWithdrawal += amount;
    this.balance -= amount;
    this.balance = Math.round(this.balance * 100) / 100;
  }

  finalizeMonth(receipt: SavingsReceipt): void {
    const month = this.transactions.length + 1;
    const netAmountAdded =
      Math.round(
        (this._currentMonthDeposit - this._currentMonthWithdrawal) * 100,
      ) / 100;

    this.transactions.push({
      month,
      amountAdded: netAmountAdded,
      interestAdded: receipt.interest,
      balanceAfter: this.balance,
    });

    // Reset for next month
    this._currentMonthDeposit = 0;
    this._currentMonthWithdrawal = 0;
  }
}

export class PlannedInterestAccount {
  readonly name: string;
  readonly initialBalance: number;
  readonly interestRate: number;
  readonly interestCompounding: Compounding;
  readonly plannedContributionAmount: number | undefined;
  readonly plannedContributionFrequency: Compounding | undefined;
  private _transactions: SavingsTransaction[];
  private _simulatedUpTo: number;
  private _balance: number;
  private _unpaidInterest: number;

  constructor(config: SavingsAccountParams) {
    this.name = config.name ?? "Savings Account";
    this.initialBalance = Math.round((config.initialAmount ?? 0) * 100) / 100;
    this.interestRate = config.interestRate ?? 0;
    this.interestCompounding = (config.interestCompounding ??
      "monthly") as Compounding;
    this.plannedContributionAmount = config.contributionAmount;
    this.plannedContributionFrequency = config.contributionFrequency;
    this._transactions = [];
    this._simulatedUpTo = -1;
    this._balance = this.initialBalance;
    this._unpaidInterest = 0;
  }

  balance(month: number): number {
    this._simulateTo(month);
    return this._transactions[month]?.balanceAfter ?? this._balance;
  }

  transactions(month: number): SavingsTransaction[] {
    this._simulateTo(month);
    return this._transactions.slice(0, month + 1);
  }

  private _simulateTo(month: number): void {
    if (this._simulatedUpTo >= month) return;

    let balance = this._simulatedUpTo < 0 ? this.initialBalance : this._balance;
    let transactions = this._simulatedUpTo < 0 ? [] : [...this._transactions];

    for (let m = this._simulatedUpTo + 1; m <= month; m++) {
      // Deposit at start of month/year
      let deposit = 0;
      if (
        this.plannedContributionAmount &&
        this.plannedContributionAmount > 0
      ) {
        if (this.plannedContributionFrequency === "monthly") {
          deposit = this.plannedContributionAmount;
        } else if (
          this.plannedContributionFrequency === "yearly" &&
          m % 12 === 0
        ) {
          deposit = this.plannedContributionAmount;
        }
      }

      // Interest at end of compounding period
      let interest = 0;
      if (this.interestCompounding === "monthly") {
        // For monthly compounding: add deposit first, then calculate interest
        balance += deposit;
        interest = balance * (this.interestRate / 12 / 100);
        balance += interest;
      } else if (this.interestCompounding === "yearly") {
        // For yearly compounding with yearly contributions: add deposit at year start before calculating interest
        // For yearly compounding with monthly contributions: calculate interest first, then add deposit
        if (this.plannedContributionFrequency === "yearly" && m % 12 === 0) {
          balance += deposit;
          deposit = 0; // Already added, so don't add again later
        }
        const monthlyInterest = balance * (this.interestRate / 12 / 100);
        this._unpaidInterest += monthlyInterest;
        balance += deposit;
        if (m % 12 === 11) {
          interest = this._unpaidInterest;
          balance += this._unpaidInterest;
          this._unpaidInterest = 0;
        }
        // Restore deposit value for transaction recording
        if (this.plannedContributionFrequency === "yearly" && m % 12 === 0) {
          deposit = this.plannedContributionAmount!;
        }
      }

      balance = Math.round(balance * 100) / 100;
      interest = Math.round(interest * 100) / 100;

      transactions.push({
        month: m + 1,
        amountAdded: deposit,
        interestAdded: interest,
        balanceAfter: balance,
      });
    }

    this._transactions = transactions;
    this._simulatedUpTo = month;
    this._balance = balance;
  }
}
